// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Edit from '../forms/customertypes/Edit';

import { typeClient, TypeClientDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: TypeClientDO[];
}

export default class CustomerTypes extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      del: true,
      add: true,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    this.loadingStart();
    typeClient<TypeClientDO[]>('list')
      ?.then((resp: TypeClientDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'CTE';
    const title: string = rowId ? `Customer-Type Edit: ${rowId}` : 'Customer-Type New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'CUSTOMER_TYPES',
        title: 'CustomerTypes',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: TypeClientDO): void => {
    window.store.dispatch(removeTab('CTE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: TypeClientDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      typeClient('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      name: 'Name',
    };
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
